'use client';

import { useState, useCallback } from 'react';
import { Share2, Check, Link as LinkIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface ShareButtonProps {
  title: string;
  text?: string;
  url?: string; // defaults to current page URL
  className?: string;
  variant?: 'icon' | 'button' | 'prominent';
}

export function ShareButton({ title, text, url, className = '', variant = 'button' }: ShareButtonProps) {
  const t = useTranslations('shareButton');
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const handleShare = useCallback(async () => {
    // Try native Web Share API first (works on mobile + some desktops)
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: text || title,
          url: shareUrl,
        });
        return;
      } catch (err: any) {
        // User cancelled or share failed — fall through to clipboard
        if (err.name === 'AbortError') return;
      }
    }

    // Fallback: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Last resort: select + copy via input
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [title, text, shareUrl]);

  if (variant === 'prominent') {
    return (
      <button
        onClick={handleShare}
        className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-sm font-semibold text-slate-700 transition-colors ${className}`}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-emerald-500 shrink-0" />
            <span className="text-emerald-600">{t('linkCopied')}</span>
          </>
        ) : (
          <>
            <Share2 className="w-4 h-4 shrink-0" />
            {t('shareToFriend')}
          </>
        )}
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleShare}
        className={`p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors ${className}`}
        title={t('share')}
      >
        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Share2 className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      onClick={handleShare}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors ${className}`}
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-emerald-600">{t('linkCopied')}</span>
        </>
      ) : (
        <>
          <Share2 className="w-3.5 h-3.5" />
          {t('share')}
        </>
      )}
    </button>
  );
}
